import moment from "moment";
import { useContext, useEffect } from "react";
import { SingleClassContext } from "../context/SingleClassContext";
import utils from "../components/schedule/utils";

const useSchedule = () => {
  const {
    view,
    days,
    month,
    update,
    spinner,
    setMonth,
    end_date,
    start_date,
    setEndDate,
    getSchedule,
    setStartDate,
  } = useContext(SingleClassContext);

  // Initialize start_date if not set
  useEffect(() => {
    if (!start_date || start_date === null) {
      const initialDate = utils.getStartDate(view);
      setStartDate(initialDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Update month in context when start_date changes (for compatibility)
  useEffect(() => {
    if (start_date && start_date !== null) {
      const startMoment = moment(start_date);
      const monthFromStartDate = startMoment.month();
      if (month !== monthFromStartDate) {
        setMonth(monthFromStartDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start_date]);

  // Calculate end_date from start_date
  useEffect(() => {
    if (start_date && start_date !== null) {
      const endDate = getEndDate(view, start_date);
      setEndDate(endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start_date, view]);

  // Fetch schedule when dates change
  useEffect(() => {
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [end_date, update]);

  const getEndDate = (view, start_date) => {
    if (view === "month") {
      return moment(start_date)
        .endOf("isoWeek")
        .endOf("month")
        .format("YYYY-MM-DD");
    }
    return moment(start_date)
      .endOf(view === "week" ? "isoWeek" : "month")
      .format("YYYY-MM-DD");
  };

  const fetchSchedule = (force = false) => {
    if (utils.validDates(start_date, end_date) && !spinner) {
      getSchedule(
        {
          start_date,
          end_date,
        },
        force
      );
    }
  };

  const setNextMonth = () => {
    const nextStartDate = utils.getStartNext("month", start_date);
    setStartDate(nextStartDate);
  };

  const setPrevMonth = () => {
    const prevStartDate = utils.getStartPrev("month", start_date);
    setStartDate(prevStartDate);
  };

  const handleNextWeek = () => {
    const next_week = utils.getStartNext("week", start_date);
    setStartDate(next_week);
  };

  const handlePrevWeek = () => {
    const prev_week = utils.getStartPrev("week", start_date);
    setStartDate(prev_week);
  };

  // Derive month and year from start_date for display
  const startMoment = start_date ? moment(start_date) : moment();
  const monthString = startMoment.format("MMM");
  const yearString = startMoment.format("YYYY");

  const getWeekNumber = () => {
    if (start_date && start_date !== null) {
      const startMoment = moment(start_date);
      const monthStart = startMoment.clone().startOf("month").startOf("isoWeek");
      const weekStart = startMoment.clone().startOf("isoWeek");
      const diffWeeks = weekStart.diff(monthStart, "weeks");
      return diffWeeks + 1;
    }
    return null;
  };

  const weekNumber = getWeekNumber();

  return {
    days,
    month,
    end_date,
    weekNumber,
    start_date,
    getEndDate,
    yearString,
    monthString,
    setNextMonth,
    setPrevMonth,
    fetchSchedule,
    getWeekNumber,
    handleNextWeek,
    handlePrevWeek,
  };
};

export default useSchedule;
